import moment, { tz } from 'moment';
import uuid from 'uuid';

import { setGlobalMessage } from 'containers/MessageHandler/actions';
import { NOTE_TYPE } from 'containers/OneProjectPage/MeetingPage/utils';
import { api } from 'redux/api/index.api';
import Analytics from 'utils/Analytics';

export const meetingsApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchMeetings: builder.query({
      query: ({ projectId, searchValue, limit, offset, withDeleted }) => ({
        url: `projects/${projectId}/meetings/filter`,
        method: 'POST',
        params: {
          limit,
          offset,
          with_deleted: withDeleted,
        },
        body: {
          search_term: searchValue,
        },
      }),
      providesTags: ['MeetingsList'],
    }),
    fetchMeeting: builder.query({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}`,
        method: 'GET',
      }),
      transformResponse: response => ({
        ...response,
        name: response?.description,
        meeting_start_time: response?.meeting_start_date,
        ...(response?.next_meeting_start_date && {
          next_meeting_start_time: response?.next_meeting_start_date,
        }),
      }),
      providesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: meetingId },
        { type: 'Meeting' },
      ],
    }),
    fetchMeetingSections: builder.query({
      query: ({ projectId, meetingId, limit = 1000, offset = 0, parentId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections`,
        method: 'GET',
        params: {
          limit,
          offset,
          parent_id: parentId,
        },
      }),
      transformResponse: (response, meta, arg) => {
        if (!response?.sections) return response;
        const sections = [...response.sections].sort((a, b) => a.order - b.order).map(section => ({
          ...section,
          depth: arg?.parentDepth ? arg.parentDepth + 1 : 1,
        }));
        return { ...response, sections };
      },
    }),
    fetchSectionNotes: builder.query({
      query: ({ projectId, meetingId, sectionId, limit = 1000, offset = 0 }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points`,
        method: 'GET',
        params: {
          limit,
          offset,
        },
      }),
    }),
    fetchStakeholders: builder.query({
      query: ({ projectId, meetingId, filters = {}, limit = 1000, offset = 0 }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders/filter`,
        method: 'POST',
        body: filters,
        params: {
          limit,
          offset,
        },
      }),
      providesTags: ['Stakeholders'],
    }),
    fetchAddressedPoints: builder.query({
      query: ({ projectId, filters = {}, limit, offset, withDeleted }) => {
        const newFilters = { ...filters };
        if (filters.assigned_to_me) {
          newFilters.assignee_ids = [...(filters.assignee_ids || []), filters.assigned_to_me[0]];
        }
        return {
          url: `projects/${projectId}/meetings/addressed-points/notes`,
          method: 'POST',
          body: newFilters,
          params: {
            limit,
            offset,
            with_deleted: withDeleted,
          },
        };
      },
      providesTags: ['Notes'],
    }),
    fetchAddressedPointsStatuses: builder.query({
      query: ({ projectId, lang }) => ({
        url: `projects/${projectId}/meetings/addressed-points/notes/status`,
        method: 'GET',
        params: { lang },
      }),
    }),
    fetchAddressedPoint: builder.query({
      query: ({ projectId, meetingId, sectionId, addressedPointId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${addressedPointId}/notes`,
        method: 'GET',
      }),
      providesTags: ['Notes'],
    }),
    fetchLinkedMeetings: builder.query({
      query: ({ projectId, addressedPointId }) => ({
        url: `projects/${projectId}/meetings/addressed-points/notes/${addressedPointId}/linked-meetings`,
        method: 'GET',
      }),
    }),
    createMeeting: builder.mutation({
      query: ({ projectId, moduleId, description, startDate }) => {
        const clientDate = new Date().toISOString();
        return {
          url: `projects/${projectId}/meetings/generate`,
          method: 'POST',
          body: {
            description,
            module_id: moduleId,
            meeting_start_date: startDate,
            client_created_at: clientDate,
            client_updated_at: clientDate,
          },
        };
      },
      invalidatesTags: ['MeetingsList'],
      async onQueryStarted({ projectId }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_creation_succeeded', { type: 'new' });
          }
        } catch (error) {
          console.error('Error saving meeting:', error);
        }
      },
    }),
    createSection: builder.mutation({
      query: ({ projectId, meetingId, sectionName, parentId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections`,
        method: 'POST',
        body: {
          id: uuid.v4(),
          name: sectionName,
          client_updated_at: moment(),
          client_created_at: moment(),
          parent_section_id: parentId,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_section_creation_succeeded', {
              depth: `${data.current_depth - 1}`,
              parentId: data.parent_section_id,
            });
          }
        } catch (error) {
          console.error('Error creating section:', error);
        }
      },
      transformResponse: response => ({
        ...response,
        depth: response.current_depth,
        children_sections_count: 0,
        addressed_points_count: 0,
      }),
    }),
    createNote: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteType, text, moduleNoteId, order }) => {
        const body = {
          id: uuid.v4(),
          type: noteType,
          order,
          client_updated_at: moment(),
          client_created_at: moment(),
        };
        if (noteType === NOTE_TYPE.notes) body.note = text;
        else body.linked_item_id = moduleNoteId;
        return {
          url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points`,
          method: 'POST',
          body,
        };
      },
      async onQueryStarted({ organizationId, projectId, noteType }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_remark_creation_succeeded', {
              type: noteType,
            });

            if (noteType !== NOTE_TYPE.notes) {
              dispatch(
                setGlobalMessage({
                  type: 'success',
                  intlMessage: {
                    id: 'item_linked',
                    values: { count: 1 },
                  },
                  active: true,
                }),
              );
            }
          }
        } catch (error) {
          console.error('Error creating note:', error);
        }
      },
      invalidatesTags: ['Notes'],
    }),
    createNotesCluster: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteType, notes }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/bulk-addressed-points`,
        method: 'POST',
        body: {
          addressed_points: notes.map(note => ({
            ...note,
            id: uuid.v4(),
            type: noteType,
            linked_item_id: note.itemId,
          })),
          client_updated_at: moment(),
          client_created_at: moment(),
        },
      }),
      async onQueryStarted(
        { organizationId, projectId, noteType, notes },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_remark_creation_succeeded', {
              type: noteType,
            });
          }
          dispatch(
            setGlobalMessage({
              type: 'success',
              intlMessage: {
                id: 'item_linked',
                values: { count: notes.length },
              },
              active: true,
            }),
          );
        } catch (error) {
          console.error('Error creating note:', error);
        }
      },
      invalidatesTags: ['Notes'],
    }),
    createStakeholder: builder.mutation({
      query: ({ projectId, meetingId, stakeholder }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders`,
        method: 'POST',
        body: {
          ...stakeholder,
          id: uuid.v4(),
          user_id: stakeholder.user_id,
          attendance: stakeholder.attendance,
          is_attendee: true,
          is_summoned: true,
          is_recipient: false,
          client_updated_at: moment(),
          client_created_at: moment(),
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled;
        Analytics.track('meetings_attendees_changed', {
          type: 'add',
        });
      },
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'MeetingsList' },
        { type: 'Meeting', id: meetingId },
        { type: 'Stakeholders' },
      ],
    }),
    duplicateMeetingInSerie: builder.mutation({
      query: ({ projectId, meetingSeriesId, startDate, description }) => {
        const clientDate = new Date().toISOString();
        return {
          url: `projects/${projectId}/meetings/series/${meetingSeriesId}/generate-next-meeting`,
          method: 'POST',
          body: {
            meeting_start_date: startDate,
            description,
            client_created_at: clientDate,
            client_updated_at: clientDate,
          },
        };
      },
      invalidatesTags: ['MeetingsList', 'Meeting'],
      async onQueryStarted({ projectId }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_creation_succeeded', { type: 'duplication' });
          }
        } catch (error) {
          console.error('Error duplicating meeting:', error);
        }
      },
    }),
    generateReport: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/report/generate`,
        method: 'POST',
        body: {
          time_zone: tz.guess(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
      async onQueryStarted({ projectId, meetingId }, { dispatch, queryFulfilled }) {
        try {
          dispatch(
            api.util.updateQueryData('fetchMeeting', { projectId, meetingId }, draft => {
              if (!draft) return draft;
              const updatedDraft = { ...draft };
              updatedDraft.export_status = 'Generating';
              return updatedDraft;
            }),
          );
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            dispatch(
              setGlobalMessage({
                type: 'success',
                intlMessage: {
                  id: 'meeting_generation_alert',
                },
                active: true,
              }),
            );
            Analytics.track('reports_meetings_pdf_succeeded');
          }
        } catch (error) {
          console.error(error);
        }
      },
    }),
    updateDescription: builder.mutation({
      query: ({ projectId, meetingId, description }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-description`,
        method: 'PUT',
        body: {
          description,
          client_updated_at: new Date().toISOString(),
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled;
        Analytics.track('meetings_description_changed');
      },
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        { type: 'Meeting', id: meetingId },
      ],
    }),
    updateMeetingDate: builder.mutation({
      query: ({ projectId, meetingId, meetingDate }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-meeting-date`,
        method: 'PUT',
        body: {
          meeting_start_date: meetingDate,
          meeting_end_date: new Date(
            new Date(meetingDate).getTime() + 2 * 60 * 60 * 1000,
          ).toISOString(),
          client_updated_at: new Date().toISOString(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        { type: 'Meeting', id: meetingId },
      ],
    }),
    updateNextMeetingDate: builder.mutation({
      query: ({ projectId, meetingId, nextMeetingDate }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-next-meeting-date`,
        method: 'PUT',
        body: {
          next_meeting_start_date: nextMeetingDate,
          next_meeting_end_date: new Date(
            new Date(nextMeetingDate).getTime() + 2 * 60 * 60 * 1000,
          ).toISOString(),
          client_updated_at: new Date().toISOString(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        { type: 'Meeting', id: meetingId },
      ],
    }),
    reorderSections: builder.mutation({
      query: ({ projectId, meetingId, sectionId, order, newParentId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/reorganize`,
        method: 'PUT',
        body: {
          order,
          new_parent_section_id: newParentId,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled;
        Analytics.track('meetings_section_reordered');
      },
    }),
    updateSectionName: builder.mutation({
      query: ({ projectId, meetingId, sectionId, sectionName }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/set-name`,
        method: 'PUT',
        body: {
          name: sectionName,
          client_updated_at: moment(),
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_section_renamed');
          }
        } catch (error) {
          console.error('Error updating section name:', error);
        }
      },
    }),
    updateNote: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, content, order }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/set-note`,
        method: 'PUT',
        body: {
          order,
          note: content,
          client_updated_at: moment(),
        },
      }),
      async onQueryStarted({ userRole, source }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_remark_changed', {
              count: 1,
              user_role: userRole,
              source,
            });
          }
        } catch (error) {
          console.error('Error updating note:', error);
        }
      },
      invalidatesTags: ['Notes'],
    }),
    updateStakeholderExpectedness: builder.mutation({
      query: ({ projectId, meetingId, stakeholderId, isAttendee, attendance }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders/${stakeholderId}/set-expectedness`,
        method: 'PUT',
        body: {
          is_attendee: isAttendee,
          attendance,
          client_updated_at: moment(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'MeetingsList' },
        { type: 'Meeting', id: meetingId },
        { type: 'Stakeholders' },
      ],
    }),
    updateStakeholderSummoning: builder.mutation({
      query: ({ projectId, meetingId, stakeholderId, isSummoned }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders/${stakeholderId}/set-summoning`,
        method: 'PUT',
        body: {
          is_summoned: isSummoned,
          client_updated_at: moment(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'MeetingsList' },
        { type: 'Meeting', id: meetingId },
        { type: 'Stakeholders' },
      ],
    }),
    patchAddressedPointDueDate: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, date }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/set-due-date`,
        method: 'PATCH',
        body: `"${date}"`,
      }),
      invalidatesTags: ['Notes'],
    }),
    patchAddressedPointStatus: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, statusId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/set-status`,
        method: 'PATCH',
        body: `"${statusId}"`,
      }),
      invalidatesTags: ['Notes'],
    }),
    patchAddressedPointAssignees: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, assigneeIds }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/set-assignees-to`,
        method: 'PATCH',
        body: assigneeIds,
      }),
      invalidatesTags: ['Notes'],
    }),
    patchAddressedPointCompanies: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, companyIds }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/set-assigned-companies-ids`,
        method: 'PATCH',
        body: companyIds,
      }),
      invalidatesTags: ['Notes'],
    }),
    attachAddressedPointMediaResource: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, mediaResource }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/attach-media`,
        method: 'PATCH',
        body: {
          media_resource: mediaResource,
          client_created_at: moment(),
        },
      }),
      invalidatesTags: ['Notes'],
    }),
    detachAddressedPointMediaResource: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, mediaResourceId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/detach-media`,
        method: 'PATCH',
        body: {
          media_resource_id: mediaResourceId,
        },
      }),
      invalidatesTags: ['Notes'],
    }),
    setNotePlan: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId, planId, positionX, positionY }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}/notes/set-plan`,
        method: 'PATCH',
        body: {
          plan_id: planId || null,
          position_x: positionX || 0,
          position_y: positionY || 0,
        },
      }),
      invalidatesTags: ['Notes'],
    }),
    deleteMeeting: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        'Notes',
        { type: 'Meeting', id: meetingId },
      ],
    }),
    deleteSection: builder.mutation({
      query: ({ projectId, meetingId, sectionId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_section_deleted');
          }
        } catch (error) {
          console.error('Error deleting section:', error);
        }
      },
      invalidatesTags: ['Notes'],
    }),
    deleteNote: builder.mutation({
      query: ({ projectId, meetingId, sectionId, noteId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/sections/${sectionId}/addressed-points/${noteId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_remark_deleted');
          }
        } catch (error) {
          console.error('Error deleting note:', error);
        }
      },
      invalidatesTags: ['Notes'],
    }),
    lockMeeting: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/lock`,
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled;
        Analytics.track('meetings_meeting_state', {
          state: 'locked',
        });
      },
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        { type: 'Meeting', id: meetingId },
      ],
    }),
    unlockMeeting: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/unlock`,
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled;
        Analytics.track('meetings_meeting_state', {
          state: 'unlocked',
        });
      },
      invalidatesTags: (result, error, { meetingId }) => [
        'MeetingsList',
        { type: 'Meeting', id: meetingId },
      ],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchMeetingsQuery,
  useFetchMeetingQuery,
  useLazyFetchMeetingQuery,
  useLazyFetchMeetingSectionsQuery,
  useFetchSectionNotesQuery,
  useFetchAddressedPointsQuery,
  useFetchAddressedPointsStatusesQuery,
  useFetchAddressedPointQuery,
  useFetchLinkedMeetingsQuery,
  useLazyFetchLinkedMeetingsQuery,
  useLazyFetchSectionNotesQuery,
  useFetchStakeholdersQuery,
  useCreateMeetingMutation,
  useCreateSectionMutation,
  useCreateNoteMutation,
  useCreateNotesClusterMutation,
  useCreateStakeholderMutation,
  useDuplicateMeetingInSerieMutation,
  useGenerateReportMutation,
  useUpdateDescriptionMutation,
  useUpdateMeetingDateMutation,
  useUpdateNextMeetingDateMutation,
  useReorderSectionsMutation,
  useUpdateSectionNameMutation,
  useUpdateNoteMutation,
  useUpdateStakeholderExpectednessMutation,
  useUpdateStakeholderSummoningMutation,
  usePatchAddressedPointDueDateMutation,
  usePatchAddressedPointStatusMutation,
  usePatchAddressedPointAssigneesMutation,
  usePatchAddressedPointCompaniesMutation,
  useAttachAddressedPointMediaResourceMutation,
  useDetachAddressedPointMediaResourceMutation,
  useSetNotePlanMutation,
  useDeleteMeetingMutation,
  useDeleteSectionMutation,
  useDeleteNoteMutation,
  useLockMeetingMutation,
  useUnlockMeetingMutation,
} = meetingsApi;
